import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { LoadingState } from '../../components/ui/LoadingState';
import { StatsCard } from '../../components/ui/StatsCard';
import { useAuth } from '../../contexts/AuthContext';

export default function Organization() {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);

  const orgId = user?.organization?.id;
  const organization = user?.organization;

  // Fetch full organization details from API
  const { data: orgDetails } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: () => apiService.getOrganization(String(orgId)),
    enabled: !!orgId,
  });

  const totalUsers = orgDetails?.total_users ?? '—';
  const analysesThisMonth = orgDetails?.analyses_this_month ?? orgDetails?.settings?.analyses_this_month ?? '—';
  const storageUsed = orgDetails?.storage_used ?? orgDetails?.settings?.storage_used ?? '—';
  const storageLimit = orgDetails?.storage_limit ?? orgDetails?.settings?.storage_limit ?? '—';
  const analysesLimit = orgDetails?.analyses_limit ?? orgDetails?.settings?.analyses_limit ?? '—';

  if (!organization) {
    return <LoadingState message="Loading organization..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Organization Settings</h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage your organization's settings, subscription, and usage limits
        </p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title="Total Users"
          value={String(totalUsers)}
          subtitle={typeof totalUsers === 'number' ? `${totalUsers} users in organization` : undefined}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          }
        />
        <StatsCard
          title="Analyses This Month"
          value={String(analysesThisMonth)}
          subtitle={analysesLimit !== '—' ? `Out of ${analysesLimit} limit` : undefined}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          }
        />
        <StatsCard
          title="Storage Used"
          value={String(storageUsed)}
          subtitle={storageLimit !== '—' ? `Out of ${storageLimit}` : undefined}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
              />
            </svg>
          }
        />
      </div>

      {/* Organization Details */}
      <Card variant="elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>Basic information about your organization</CardDescription>
            </div>
            {!isEditing ? (
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setIsEditing(false)}>Save Changes</Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Input
              label="Organization Name"
              value={organization.name}
              disabled={!isEditing}
              required
            />
            <Input
              label="Organization Slug"
              value={organization.slug}
              helpText="Used in URLs and API calls"
              disabled={!isEditing}
              required
            />
            <Input label="Contact Email" type="email" disabled={!isEditing} />
          </div>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Subscription Plan</CardTitle>
          <CardDescription>Manage your subscription tier and limits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-700">Current Plan</p>
                <p className="text-2xl font-bold text-gray-900 capitalize">
                  {organization.subscription_tier}
                </p>
              </div>
              <Badge variant="primary" size="lg">
                {organization.subscription_tier}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Plan Limits</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Up to 100 users
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    10,000 analyses per month
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    100 GB storage
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Advanced AI features
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-primary-50 border-2 border-primary-200 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Need More?</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Upgrade to Enterprise for unlimited users, analyses, and premium support.
                </p>
                <Button variant="primary" size="sm">
                  Upgrade Plan
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Security Settings</CardTitle>
          <CardDescription>Configure security policies for your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <div>
                <p className="font-medium text-gray-900">Two-Factor Authentication</p>
                <p className="text-sm text-gray-600">Require 2FA for all users</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <div>
                <p className="font-medium text-gray-900">SSO / SAML</p>
                <p className="text-sm text-gray-600">Enable Single Sign-On</p>
              </div>
              <Badge variant="warning">Enterprise Only</Badge>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-900">IP Whitelist</p>
                <p className="text-sm text-gray-600">Restrict access to specific IP addresses</p>
              </div>
              <Button variant="outline" size="sm">
                Configure
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card variant="bordered">
        <CardHeader>
          <CardTitle className="text-danger-600">Danger Zone</CardTitle>
          <CardDescription>Irreversible actions for your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-danger-50 border border-danger-200 rounded-lg">
              <div>
                <p className="font-medium text-danger-900">Delete Organization</p>
                <p className="text-sm text-danger-700">
                  Permanently delete this organization and all its data
                </p>
              </div>
              <Button variant="danger">Delete Organization</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
